import { fail } from '@/features/menu/utils/product-page-actions';
import { pretty, toggleIn } from '@/features/menu/utils/product-helpers';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import { useEffect, useState } from 'react';
import { List, ListItem, PageContent } from 'framework7-react';

// ─── Code-set edit form (allergens / labels) ─────────────────────────────────

type CodesSubmit<T extends string> = (
  codes: T[],
  done: () => void,
  errOut: (e: unknown) => void,
) => void;

export default function CodesSheetContent<T extends string>({
  open,
  onClose,
  all,
  initial,
  onSubmit,
  submitRef,
}: {
  open: boolean;
  onClose: () => void;
  all: readonly T[];
  initial: readonly T[];
  onSubmit: CodesSubmit<T>;
  submitRef: { current: () => void };
}) {
  const [sel, setSel] = useState<T[]>([]);
  useEffect(() => {
    if (open) setSel([...initial]);
  }, [open, initial]);

  useUnsavedGuard(open && JSON.stringify([...sel].sort()) !== JSON.stringify([...initial].sort()));

  submitRef.current = () => onSubmit(sel, onClose, fail);

  // iOS-native "selectable list" - every code is a row, with a trailing
  // checkmark when selected (same pattern as iOS Settings → Language, Ringtone…).
  // Easier to scan than a chip grid and the selected state is unambiguous.
  return (
    <PageContent>
      <List strongIos insetIos dividersIos>
        {all.map((code) => {
          const on = sel.includes(code);
          return (
            <ListItem
              key={code}
              title={pretty(code)}
              link="#"
              noChevron
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                setSel((cur) => toggleIn(cur, code));
              }}
            >
              {on && (
                <i
                  slot="after"
                  className="f7-icons"
                  style={{ color: 'var(--f7-theme-color)', fontSize: 22 }}
                >
                  checkmark
                </i>
              )}
            </ListItem>
          );
        })}
      </List>
    </PageContent>
  );
}
